import { useState, useEffect } from "react";
import { webLightTheme, Spinner, TableCell, TableBody, TableRow, Table, TableHeader, TableHeaderCell, useId, Label, Button, makeStyles, Input, CardHeader, Text} from "@fluentui/react-components";
import { ControlPanelNavigation } from './control-panel-navigation.js';
import { PersonRegular, KeyRegular, PersonAddRegular, Patient20Regular } from "@fluentui/react-icons";
import { API_PATH } from '../Constants.ts';

const useStyles = makeStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",

      // Prevent the example from taking the full width of the page (optional)
      // maxWidth: "400px",

      // Stack the label above the field (with 2px gap per the design system)
      "> div": { display: "flex", flexDirection: "column", gap: "2px" },
    },
    inner: {
        maxWidth: "400px",
        marginTop: "10px",
        // Apply styles to all child divs inside the form
        "& > div": {
            marginBottom: "5px",
        },
    }
});

// selected columns from table "users"
const shownColumns = [
    { columnKey: "id", label: "#" },
    { columnKey: "username", label: "username" },
    { columnKey: "first_name", label: "First name" },
    { columnKey: "last_name", label: "Last Name" },
    { columnKey: "titles_before_name", label: "Title Before" },
    { columnKey: "titles_after_name", label: "Title After" },
];

export const Patients = () => {
    const styles = useStyles();

    // fetch users from the API to display in the table
    const [users, setUsers] = useState([]);
    const fetchUsers = async () => {
        try {
          const response = await fetch(API_PATH.USERS, { headers: {'Cache-Control': 'no-cache'} });
          if (!response.ok) {
            console.error(`HTTP Error: ${response.status} ${response.statusText}`);
            return;
          }

          const data = await response.json();
          console.log('Success:', data);
          setUsers(data);
        } catch (error) {
          console.error('Network Error:', error.message || error);
        }
    };

    useEffect(() => {
        fetchUsers();
        const timer = setInterval(fetchUsers, 10000);
        return () => clearInterval(timer);
      }, []);

    // used in form for adding new patient
    const usernameId = useId("username-id");
    const passwordId = useId("password-id");
    const titleBeforeId = useId("title-before-id");
    const firstNameId = useId("first-name-id");
    const lastNameId = useId("last-name-id");
    const titleAfterId = useId("title-after-id");

    // insert a new patient into the database
    const [processing, setProcessing] = useState(false);
    const handleSubmit = (event) => {
        event.preventDefault();
        setProcessing(true);

        const formData = new FormData(event.target);
        const newPatientInfo = Object.fromEntries(formData.entries());
        newPatientInfo.role = 'vr'; // security does not rely on this
        
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
            body: JSON.stringify(newPatientInfo)
        };

        fetch(API_PATH.USERS, requestOptions)
        .then(async response => {
            const data = await response.json();
            console.log('Success:', data);
        })
        .catch(error => {
            console.error(error);
        }).finally(() => {
            fetchUsers(); // refresh the list of users
            setProcessing(false);
            console.log('Form submitted');
        });
      };

    return (
        <div className='control-panel-layout'>
            <aside className="sidenav">
                <ControlPanelNavigation />
            </aside>
            <div className={styles.root}>
                {processing && <Spinner size="small" label="Creating new patient..." style={{ background: webLightTheme.colorNeutralBackground1, padding: '10px' }} />}
                <form onSubmit={handleSubmit} className={processing ? "displayNone" : styles.inner}>
                    <div>
                        <Label htmlFor={usernameId}></Label>
                        <Input name="username" id={usernameId} placeholder="Enter username" contentBefore={<PersonRegular />} />
                    </div>

                    <div>
                        <Label htmlFor={passwordId}></Label>
                        <Input name="password" id={passwordId} placeholder="Enter password" contentBefore={<KeyRegular />} type="password" />
                    </div>

                    <div>
                        <Label htmlFor={titleBeforeId}></Label>
                        <Input name="titles_before_name" id={titleBeforeId} placeholder="Enter title before name" />
                    </div>

                    <div>
                        <Label htmlFor={firstNameId}></Label>
                        <Input name="first_name" id={firstNameId} placeholder="Enter first name" />
                    </div>

                    <div>
                        <Label htmlFor={lastNameId}></Label>
                        <Input name="last_name" id={lastNameId} placeholder="Enter last name" />
                    </div>

                    <div>
                        <Label htmlFor={titleAfterId}></Label>
                        <Input name="titles_after_name" id={titleAfterId} placeholder="Enter title after name" />
                    </div>

                    <Button as="button" type="submit" appearance="primary" icon={<PersonAddRegular />}>Add Patient</Button>
                </form>

                <div className="main-cards">
                    <CardHeader image={<Patient20Regular />} header={<Text weight="semibold">Patients</Text>} />
                    <Table noNativeElements aria-label="Table with selection" aria-rowcount={users.length}>
                        <TableHeader>
                            <TableRow>
                                {shownColumns.map((column) => (
                                <TableHeaderCell key={column.columnKey}>
                                    {column.label}
                                </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map(
                                        (item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>{item.id}</TableCell>
                                            <TableCell>{item.username}</TableCell>
                                            <TableCell>{item.first_name}</TableCell>
                                            <TableCell>{item.last_name}</TableCell>
                                            <TableCell>{item.titles_before_name}</TableCell>
                                            <TableCell>{item.titles_after_name}</TableCell>
                                        </TableRow>
                                        )
                                    )
                            }
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};
